import { Link } from "react-router-dom";
import { SEO } from "@/components/SEO";
import {
  CheckCircle2,
  XCircle,
  Calculator,
  CalendarClock,
  CreditCard,
  ShieldCheck,
  ArrowRight,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";

export default function Financing() {
  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "What credit score do I need to finance a roof in Saskatchewan?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "While requirements vary by lender, most of our financing partners look for a credit score of 650 or higher for the most competitive rates. However, there are flexible options available even if your credit isn't perfect. We recommend applying to see exactly what you qualify for without affecting your credit score.",
          },
        },
        {
          "@type": "Question",
          name: "Are there any hidden fees or early repayment penalties?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "No. The financing options we offer through our partners feature fully open loans. This means you can pay off your roof early at any time without facing any prepayment penalties or hidden fees. You only pay interest for the time you hold the loan.",
          },
        },
        {
          "@type": "Question",
          name: "How quickly can I get approved for roof financing?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "The approval process is typically very fast. Most homeowners receive a credit decision within minutes of submitting their application. Once approved, the funds are made available so we can schedule your roof replacement right away, which is especially helpful during emergencies like storm damage.",
          },
        },
      ],
    },
  ];

  return (
    <div className="flex flex-col min-h-screen">
      <SEO
        title="Roofing Financing Saskatchewan | Sask Roofing Company"
        description="Flexible roof replacement financing options in Saskatchewan. Get a new roof today with affordable monthly payment plans. Book a free inspection to learn more."
        schema={schema}
      />

      {/* Hero Section */}
      <section className="relative py-24 bg-primary text-primary-foreground overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/0a338768-7490-4ad4-b966-bf58233c22a6.png&output=webp&q=60"
            alt="Modern Saskatchewan ranch-style home exterior"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/60 to-primary/40" />
        </div>
        <div className="container px-4 relative z-10">
          <div className="max-w-3xl space-y-6">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight">
              Roofing Financing Saskatchewan
            </h1>
            <p className="text-xl text-primary-foreground/90 font-medium leading-relaxed">
              We understand that a roof replacement is a major investment. When
              your home needs a new roof, waiting isn't always an option. Our
              flexible financing plans help you protect your home now while
              spreading the cost over manageable monthly payments. Don't let
              budget constraints leave your home vulnerable to Saskatchewan's
              harsh weather.
            </p>
            <div className="flex flex-wrap gap-4 pt-4">
              <Button
                asChild
                size="lg"
                className="bg-accent text-accent-foreground hover:bg-accent/90"
              >
                <Link to="/contact">Book a Free Inspection</Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="bg-transparent border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/10"
              >
                <Link to="/calculator">Estimate Your Cost First</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* TL;DR Box */}
      <section className="py-12 bg-background">
        <div className="container px-4">
          <div className="max-w-4xl mx-auto bg-blue-50/50 border border-blue-100 rounded-2xl p-8 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-2 h-full bg-blue-500" />
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center shrink-0">
                <ShieldCheck className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold mb-2 text-blue-900">
                  The Short Version
                </h2>
                <p className="text-blue-800/80 leading-relaxed">
                  Yes, we offer flexible financing options through trusted
                  lending partners. Most Saskatchewan homeowners can secure a
                  new roof for as little as $150 to $300 per month, depending on
                  the scope of the project and the chosen terms. The application
                  process takes minutes, and loans are fully open—meaning you
                  can pay them off early with zero penalties.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How Financing Works */}
      <section className="py-16 bg-muted/30">
        <div className="container px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold mb-6">
                  How Roof Financing Works
                </h2>
                <div className="space-y-6 text-lg text-muted-foreground">
                  <p>
                    Securing financing for your new roof is a straightforward
                    process designed to get your home protected as quickly as
                    possible. We partner with reputable Canadian lenders who
                    specialize in home improvement loans, ensuring you get
                    competitive rates and transparent terms.
                  </p>
                  <p>
                    The process begins with a free, no-obligation roof
                    inspection. We assess your roof's condition and provide a
                    detailed, fixed-price quote. Once you know the exact cost of
                    your project, you can apply for financing through our
                    partner's secure online portal. The credit check is
                    fast—often providing an approval decision within minutes.
                  </p>
                  <p>
                    You can choose from a variety of payment plan structures,
                    typically ranging from 12 to 60 months. This flexibility
                    allows you to select a monthly payment that comfortably fits
                    your household budget. Because the loans are fully open, you
                    have the freedom to make lump-sum payments or pay off the
                    entire balance early without facing any hidden fees or early
                    repayment penalties.
                  </p>
                </div>
              </div>
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-tr from-primary/10 to-transparent rounded-3xl transform translate-x-4 translate-y-4" />
                <img
                  src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/7f87532f-9d0c-490e-b9d7-50d51aad97c6.png&output=webp&q=60"
                  alt="Completed asphalt shingle roof project in Saskatchewan"
                  className="relative rounded-3xl shadow-xl w-full object-cover aspect-[4/3]"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-16 bg-background">
        <div className="container px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">
              Example Payment Plans
            </h2>
            <p className="text-center text-muted-foreground mb-8">
              Based on a typical $10,000 roof replacement project. These figures
              are estimates for illustrative purposes; actual rates depend on
              your credit profile and current lender terms.
            </p>
            <div className="overflow-x-auto rounded-xl border shadow-sm">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-muted/50">
                    <th className="p-4 font-bold border-b">Term Length</th>
                    <th className="p-4 font-bold border-b">
                      Estimated Monthly Payment
                    </th>
                    <th className="p-4 font-bold border-b">Best For...</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="hover:bg-muted/30 transition-colors">
                    <td className="p-4 border-b font-medium">12 Months</td>
                    <td className="p-4 border-b text-primary font-bold">
                      ~$880 / mo
                    </td>
                    <td className="p-4 border-b text-muted-foreground">
                      Paying off quickly while avoiding a massive upfront cash
                      hit.
                    </td>
                  </tr>
                  <tr className="hover:bg-muted/30 transition-colors bg-blue-50/30">
                    <td className="p-4 border-b font-medium">24 Months</td>
                    <td className="p-4 border-b text-primary font-bold">
                      ~$460 / mo
                    </td>
                    <td className="p-4 border-b text-muted-foreground">
                      A balanced approach with manageable payments and lower
                      total interest.
                    </td>
                  </tr>
                  <tr className="hover:bg-muted/30 transition-colors">
                    <td className="p-4 border-b font-medium">36 Months</td>
                    <td className="p-4 border-b text-primary font-bold">
                      ~$320 / mo
                    </td>
                    <td className="p-4 border-b text-muted-foreground">
                      Comfortable monthly budgeting for standard asphalt roof
                      replacements.
                    </td>
                  </tr>
                  <tr className="hover:bg-muted/30 transition-colors">
                    <td className="p-4 border-b font-medium">60 Months</td>
                    <td className="p-4 border-b text-primary font-bold">
                      ~$210 / mo
                    </td>
                    <td className="p-4 border-b text-muted-foreground">
                      Maximizing cash flow or financing premium materials like
                      metal roofing.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-sm text-muted-foreground text-center mt-4">
              * Rates and payments are subject to credit approval. An exact
              quote will be provided during the application process.
            </p>
          </div>
        </div>
      </section>

      {/* Who Financing Is For */}
      <section className="py-16 bg-muted/30">
        <div className="container px-4">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Who Financing Is For</h2>
            <p className="text-lg text-muted-foreground">
              Financing isn't just for homeowners who don't have savings. It's a
              strategic tool used in several common scenarios.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="bg-card p-8 rounded-2xl border shadow-sm">
              <div className="w-12 h-12 bg-primary/10 text-primary rounded-xl flex items-center justify-center mb-6">
                <CalendarClock className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold mb-3">Emergency Replacements</h3>
              <p className="text-muted-foreground">
                When a roof fails unexpectedly or sustains severe storm damage,
                you can't wait months to save up. Financing gets your home
                protected immediately before interior water damage occurs.
              </p>
            </div>
            <div className="bg-card p-8 rounded-2xl border shadow-sm">
              <div className="w-12 h-12 bg-primary/10 text-primary rounded-xl flex items-center justify-center mb-6">
                <ShieldCheck className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold mb-3">Insurance Deductibles</h3>
              <p className="text-muted-foreground">
                If insurance is covering a hail damage claim but you have a high
                deductible ($2,000+), financing can help bridge the gap so the
                work can proceed without delay.
              </p>
            </div>
            <div className="bg-card p-8 rounded-2xl border shadow-sm">
              <div className="w-12 h-12 bg-primary/10 text-primary rounded-xl flex items-center justify-center mb-6">
                <CreditCard className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold mb-3">Upgrading Materials</h3>
              <p className="text-muted-foreground">
                Homeowners who want the 50-year longevity of a metal roof but
                only have cash for asphalt often use financing to cover the
                difference, investing in a permanent solution.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pros & Cons / Numbered List */}
      <section className="py-16 bg-background">
        <div className="container px-4">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12">
            {/* Pros/Cons */}
            <div>
              <h2 className="text-3xl font-bold mb-8">
                Paying Cash vs. Financing
              </h2>
              <div className="space-y-6">
                <div className="bg-card border rounded-2xl p-6 shadow-sm">
                  <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-green-100 text-green-600 flex items-center justify-center">
                      <CheckCircle2 className="w-5 h-5" />
                    </span>
                    Using Financing
                  </h3>
                  <ul className="space-y-3 text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0" />
                      <span>Keeps your emergency cash reserves intact.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0" />
                      <span>
                        Allows you to afford higher-quality, longer-lasting
                        materials.
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0" />
                      <span>
                        Gets the roof fixed instantly, preventing costly
                        interior damage.
                      </span>
                    </li>
                  </ul>
                </div>
                <div className="bg-card border rounded-2xl p-6 shadow-sm">
                  <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center">
                      <Calculator className="w-5 h-5" />
                    </span>
                    Paying Upfront
                  </h3>
                  <ul className="space-y-3 text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0" />
                      <span>Zero interest paid over the life of the roof.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0" />
                      <span>No monthly payment obligations to manage.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0" />
                      <span>
                        The best choice if you have ample liquid savings
                        dedicated to home repairs.
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* 3 Questions */}
            <div>
              <h2 className="text-3xl font-bold mb-8">
                3 Questions to Ask Yourself
              </h2>
              <div className="space-y-8">
                <div className="flex gap-4">
                  <div className="text-4xl font-bold text-primary/20 leading-none">
                    01
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">
                      Can my roof wait?
                    </h3>
                    <p className="text-muted-foreground">
                      If your roof is actively leaking or has missing shingles,
                      waiting to save cash will likely cost you more in
                      structural rot, drywall repair, and mold remediation than
                      you would ever pay in financing interest.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="text-4xl font-bold text-primary/20 leading-none">
                    02
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">
                      How long will I live here?
                    </h3>
                    <p className="text-muted-foreground">
                      If this is your forever home, financing a premium
                      architectural shingle or metal roof makes sense because
                      you will reap the 30 to 50-year benefits. If you're
                      selling next year, a standard repair or basic replacement
                      might be better.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="text-4xl font-bold text-primary/20 leading-none">
                    03
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">
                      Does the math work?
                    </h3>
                    <p className="text-muted-foreground">
                      Look at your monthly budget. Can you comfortably absorb a
                      $200-$300 monthly payment for the next few years to ensure
                      your home is completely watertight and secure?
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Next Steps */}
      <section className="py-16 bg-muted/30">
        <div className="container px-4">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h2 className="text-3xl font-bold">
              Next Steps: Get Your Quote First
            </h2>
            <p className="text-lg text-muted-foreground">
              You can't finance a roof until you know exactly what it will cost.
              The first step is always a thorough, free inspection. We'll assess
              your roof, listen to your concerns, and provide a detailed written
              quote. Once you have the numbers, we can walk you through the
              financing application process if you choose to go that route.
            </p>
            <div className="pt-4">
              <Button
                asChild
                size="lg"
                className="bg-accent text-accent-foreground hover:bg-accent/90"
              >
                <Link to="/contact">Schedule Your Free Inspection</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-background">
        <div className="container px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              Frequently Asked Questions
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {schema[0].mainEntity.map((faq: any, index: number) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-left text-lg font-bold">
                    {faq.name}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-lg leading-relaxed data-[state=closed]:hidden">
                    {faq.acceptedAnswer.text}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-20 bg-primary text-primary-foreground text-center">
        <div className="container px-4">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            See Your Financing Options. Get a Quote First.
          </h2>
          <p className="text-xl text-primary-foreground/80 mb-10 max-w-2xl mx-auto">
            Our local Saskatchewan crews are ready to provide an honest
            assessment of your roof. No high-pressure sales, just clear facts
            and flexible payment plans.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button
              asChild
              size="lg"
              className="bg-accent text-accent-foreground hover:bg-accent/90 text-lg px-8"
            >
              <Link to="/contact">Get a Free Inspection</Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-primary-foreground/20 bg-transparent text-primary-foreground hover:bg-primary-foreground/10 text-lg px-8"
            >
              <a href="tel:6397397969">Call (639) 739-7969</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
